/**
 * Jubly Calendar React Hooks
 * Data fetching and state management for calendar operations
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE = '/api';

/**
 * Hook for managing calendars
 */
export function useCalendars() {
  const [calendars, setCalendars] = useState([]);
  const [sharedCalendars, setSharedCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCalendars = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/calendars`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch calendars');
      
      const data = await response.json();
      setCalendars(data.calendars || []);
      setSharedCalendars(data.shared || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  const createCalendar = async (calendarData) => {
    const response = await fetch(`${API_BASE}/calendars`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(calendarData)
    });
    
    if (!response.ok) throw new Error('Failed to create calendar');
    
    const newCalendar = await response.json();
    setCalendars(prev => [...prev, newCalendar]);
    return newCalendar;
  };

  const updateCalendar = async (id, updates) => {
    const response = await fetch(`${API_BASE}/calendars/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) throw new Error('Failed to update calendar');
    
    const updated = await response.json();
    setCalendars(prev => prev.map(c => c.id === id ? updated : c));
    return updated;
  };

  const deleteCalendar = async (id) => {
    const response = await fetch(`${API_BASE}/calendars/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to delete calendar');
    
    setCalendars(prev => prev.filter(c => c.id !== id));
  };

  return {
    calendars,
    sharedCalendars,
    loading,
    error,
    refresh: fetchCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar
  };
}

/**
 * Hook for managing events
 */
export function useEvents(dateRange) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async () => {
    if (!dateRange?.start || !dateRange?.end) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        start: dateRange.start.toISOString(),
        end: dateRange.end.toISOString()
      });
      
      const response = await fetch(`${API_BASE}/events?${params}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Poll every 30 seconds for updates
  useEffect(() => {
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  const createEvent = async (eventData) => {
    const response = await fetch(`${API_BASE}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(eventData)
    });
    
    if (!response.ok) throw new Error('Failed to create event');
    
    const newEvent = await response.json();
    setEvents(prev => [...prev, newEvent]);
    return newEvent;
  };

  const updateEvent = async (id, updates) => {
    const response = await fetch(`${API_BASE}/events/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) throw new Error('Failed to update event');
    
    const updated = await response.json();
    setEvents(prev => prev.map(e => e.id === id ? updated : e));
    return updated;
  };

  const deleteEvent = async (id) => {
    const response = await fetch(`${API_BASE}/events/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to delete event');
    
    setEvents(prev => prev.filter(e => e.id !== id));
  };

  return {
    events,
    loading,
    error,
    refresh: fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent
  };
}

/**
 * Hook for a single event
 */
export function useEvent(eventId) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId) {
      setEvent(null);
      setLoading(false);
      return;
    }

    const fetchEvent = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/events/${eventId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Failed to fetch event');
        
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  const addAttendees = async (attendees) => {
    const response = await fetch(`${API_BASE}/events/${eventId}/attendees`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ attendees })
    });
    
    if (!response.ok) throw new Error('Failed to add attendees');
    
    const added = await response.json();
    setEvent(prev => ({
      ...prev,
      attendees: [...(prev.attendees || []), ...added]
    }));
    return added;
  };

  const removeAttendee = async (attendeeId) => {
    const response = await fetch(`${API_BASE}/events/${eventId}/attendees/${attendeeId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) throw new Error('Failed to remove attendee');
    
    setEvent(prev => ({
      ...prev,
      attendees: prev.attendees?.filter(a => a.id !== attendeeId) || []
    }));
  };

  return {
    event,
    loading,
    error,
    addAttendees,
    removeAttendee
  };
}

/**
 * Hook for calendar view state
 */
export function useCalendarView() {
  const [searchParams, setSearchParams] = useSearchParams();
  // month, week, day, agenda — remembered per user (URL param wins, then last choice)
  const [view, _setView] = useState(() => searchParams.get('view') || localStorage.getItem('jubly_calendar_view') || 'month');
  const setView = useCallback((v) => { _setView(v); try { localStorage.setItem('jubly_calendar_view', v); } catch { /* ignore */ } }, []);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEventId, _setSelectedEventId] = useState(() => searchParams.get('eventId') || null);

  // Sync selectedEventId to URL for deep linking
  const setSelectedEventId = useCallback((id) => {
    _setSelectedEventId(id);
    const params = new URLSearchParams(searchParams);
    if (id) { params.set('eventId', id); } else { params.delete('eventId'); }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const goToToday = () => setCurrentDate(new Date());
  
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month' || view === 'agenda') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month' || view === 'agenda') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  // Calculate date range based on view
  const getDateRange = useCallback(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (view === 'month') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
    } else if (view === 'week') {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + (6 - day));
      end.setHours(23, 59, 59, 999);
    } else if (view === 'agenda') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 2);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  }, [currentDate, view]);

  return {
    view,
    setView,
    currentDate,
    setCurrentDate,
    selectedEventId,
    setSelectedEventId,
    goToToday,
    goToPrevious,
    goToNext,
    getDateRange
  };
}
